import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { DynamicTag, Spinner, JsonLd, formatProduct } from 'msp-components'
import {
  parse as parseSearchParams,
  useEinstein,
  useCommerceAPI,
  productUrlBuilder,
  useMultiSite,
  useCookie,
  useSearchParams,
  useActiveData
} from 'msp-integrations'
import PageLayout from '@/components/cms/page-layout'
import LineItemProduct from '@/components/organisms/LineItemProduct/LineItemProduct'
import { JasminSearchBox } from '@/components/organisms/JasminSearchBox'
import { ContentSearch } from '@/components/molecules/ContentSearch'
import Seo from '@/components/seo'

const Search = ({ searchQuery, productSearchResult, page }: any) => {
  const [hasProductResults, setHasProductResults] = useState(
    productSearchResult?.total > 0
  )
  const { buildUrl } = useMultiSite()
  const [showNoResultsMessage, setShowNoResultsMessage] = useState(false)
  const [searchTerm, setSearchTerm] = useState(searchQuery)
  const [products, setProducts] = useState(productSearchResult?.hits || [])
  const [showSpinner, setShowSpinner] = useState(false)
  const [isSearchingProducts, setIsSearchingProducts] = useState(true)
  const [isSearchingContent, setIsSearchingContent] = useState(true)
  const { formatMessage } = useIntl()
  const { getCookieAllowedById } = useCookie()
  const einstein = useEinstein()
  const [searchParams] = useSearchParams()
  const activeData = useActiveData()

  const isSalesforceAllowed = getCookieAllowedById('s291') // CONSENTS['s291'] = 'salesforce'

  const ProductSearchResults = ({ products }: Record<string, []>) => {
    return products
      .filter((r: any) => !r.isError)
      .map((row: any) => (
        <LineItemProduct
          key={row.productId || row.id}
          product={row}
          type='XS'
          onClick={() => {
            einstein.sendClickSearch(searchQuery, row)
          }}
        />
      ))
  }

  useEffect(() => {
    setShowSpinner(isSearchingProducts || isSearchingContent)
  }, [isSearchingProducts, isSearchingContent])

  useEffect(() => {
    const filteredHits = (productSearchResult?.hits || []).filter(
      (product: any) => !product.c_showSuggestionHeadless
    )
    setProducts(filteredHits)
    setHasProductResults(filteredHits.length > 0)
    setIsSearchingProducts(!productSearchResult ? true : false)
    setSearchTerm(searchQuery)
    if (productSearchResult) {
      productSearchResult.hits = filteredHits
      einstein.sendViewSearch(searchQuery, productSearchResult)
      activeData.sendViewSearch(searchParams, productSearchResult)
    }
  }, [productSearchResult, searchQuery, isSalesforceAllowed])

  return (
    <div className='searchPage-main'>
      <Seo
        title={
          page.found && page?.meta?.title
            ? `${page?.meta?.title} ${searchTerm}`
            : formatMessage(
                {
                  id: 'msp.search.seo.title',
                  defaultMessage: 'Search for {searchTerm}'
                },
                { searchTerm: searchTerm || '' }
              )
        }
        description={
          page.found && page?.meta?.description
            ? page?.meta?.description
            : formatMessage({
                id: 'msp.search.seo.description',
                defaultMessage: 'Search for products and content on DARQ.'
              })
        }
        keywords={
          page.found && page?.meta?.keywords ? page?.meta?.keywords : ''
        }
        canonicalPath={`/search?q=${searchTerm}`}
      />
      {products && (
        <JsonLd
          type='SearchResultsPage'
          object={products?.map((p) =>
            formatProduct({
              ...p,
              slugUrl: buildUrl(
                productUrlBuilder({
                  id: p.productId || p.id,
                  name: p.name || p.productName,
                  primaryCategoryName: p.c_primaryCategoryName,
                  classCategoryName: p.c_classCategoryName,
                  primaryCategoryPath: p.c_primaryCategoryPath,
                  classCategoryPath: p.c_classCategoryPath
                })
              ),
              image: p?.imageGroups?.[0]?.images[0]?.link
            })
          )}
        />
      )}

      <div className='searchPage-container'>
        <div className='searchPage-wrapper'>
          <div className='searchPage-dynamicTagWrapper'>
            <h2 className='searchPage-headlineTag'>
              {formatMessage({
                id: 'msp.search.title',
                defaultMessage: 'Your search for'
              })}
            </h2>
          </div>
          <div className='searchPage-searchBoxWrapper'>
            <JasminSearchBox
              className='searchPage-searchBox'
              searchValue={searchTerm}
              showDeleteButton
            />
          </div>
        </div>
        {showSpinner ? (
          <Spinner size='xl' />
        ) : !hasProductResults && showNoResultsMessage ? (
          <div className='searchPage-NoResultWrapper'>
            <DynamicTag
              className='searchPage-NoResultText'
              componentTagName='Headline'
              tag='h3'
            >
              {formatMessage({
                id: 'msp.search.noresults.title',
                defaultMessage: 'Your search has not returned any results.'
              })}
            </DynamicTag>
            <DynamicTag
              className='searchPage-NoResultAdvise'
              componentTagName='Headline'
              tag='h6'
            >
              {formatMessage({
                id: 'msp.search.noresults.advise',
                defaultMessage:
                  'Please check the spelling or use a different term.'
              })}
            </DynamicTag>
          </div>
        ) : (
          products?.length > 0 && (
            <>
              <div
                className={
                  'font-Lora text-28 md:text-30 tracking-0 mb-5 mt-10 font-bold capitalize md:mb-7 md:mt-14'
                }
              >
                {formatMessage({
                  id: 'msp.search.products.title',
                  defaultMessage: 'Products'
                })}{' '}
                ({products?.length})
              </div>
              {/* @ts-ignore*/}
              <ProductSearchResults products={products} />
            </>
          )
        )}
        <ContentSearch
          className='w-full py-20'
          searchQuery={searchTerm ?? searchTerm}
          onSearch={(results: Record<string, []>) => {
            setShowNoResultsMessage(!results?.length && !hasProductResults)
          }}
          onSearching={(isSearching: boolean) =>
            setIsSearchingContent(isSearching)
          }
        />
      </div>
    </div>
  )
}

const SearchWrapper = (props: any) => {
  const api = useCommerceAPI()
  const location = useLocation()

  const urlParams = new URLSearchParams(location.search)
  const searchQuery = urlParams.get('q')
  const searchParams = parseSearchParams(location.search, false)
  // From docs
  // htype: Allow refinement by including only the provided hit types.
  // Accepted types are 'product', 'master', 'set', 'bundle', 'slicing_group' (deprecated), 'variation_group'.
  searchParams.refine.push('htype=product|set|bundle|variation_group')

  const { data: productSearchResult, isLoading } = useQuery(
    ['search-productSearch', searchParams],
    () =>
      api.shopperSearch
        .productSearch({
          parameters: searchParams
        })
        .then(async (productSearchResult: any) => {
          const promises = []
          const hits = productSearchResult?.hits || []
          hits.forEach((searchHit: any) => {
            if (searchHit.c_showSuggestionHeadless) {
              promises.push(
                api.shopperProducts.getProduct({
                  parameters: {
                    id: searchHit.productId
                  }
                })
              )
            }
          })
          productSearchResult.hits = await Promise.all(promises)

          return productSearchResult
        })
  )

  return (
    <PageLayout
      {...{
        ...props,
        ChildPage: Search,
        searchQuery,
        productSearchResult,
        isLoading
      }}
    />
  )
}

export default SearchWrapper
